import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  getNotificationsForUser,
  markAllNotificationsRead,
  markNotificationRead,
} from '../_data/quickpass-notifications'
import { QuickPassNotification } from '../_types/quickpass-notification'
import { useAuth } from './use-auth'
import {
  QuickPassNotificationsContextValue,
  QuickPassNotificationsProviderProps,
} from './quickpass-notifications-context-types'
import { QuickPassNotificationsContext } from './use-quickpass-notifications'

/**
 * Loads + manages the renter's QuickPass notifications. Mounted under
 * AuthProvider so it can read `quickPassUser.id`. Refreshes on user
 * change and exposes mark-read / mark-all-read mutators that update the
 * cache optimistically.
 *
 * @param {QuickPassNotificationsProviderProps} props
 * @returns {JSX.Element}
 */
const QuickPassNotificationsProvider = ({
  children,
}: QuickPassNotificationsProviderProps): JSX.Element => {
  const { quickPassUser, isLoading: isAuthLoading } = useAuth()
  const [notifications, setNotifications] = useState<QuickPassNotification[]>(
    [],
  )
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false)
  const lastResolvedUserIdReference = useRef<string | null>(null)

  const openDrawer = useCallback((): void => {
    setIsDrawerOpen(true)
  }, [])

  const closeDrawer = useCallback((): void => {
    setIsDrawerOpen(false)
  }, [])

  const fetchNotifications = useCallback(
    async (quickPassUserId: string): Promise<void> => {
      setIsLoading(true)

      try {
        const fetched = await getNotificationsForUser(quickPassUserId)
        setNotifications(fetched)
      } catch (error) {
        console.error('Failed to fetch notifications', error)
        setNotifications([])
      } finally {
        setIsLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (isAuthLoading === true) {
      return
    }

    if (quickPassUser === null) {
      setNotifications([])
      lastResolvedUserIdReference.current = null
      return
    }

    if (lastResolvedUserIdReference.current === quickPassUser.id) {
      return
    }

    lastResolvedUserIdReference.current = quickPassUser.id
    void fetchNotifications(quickPassUser.id)
  }, [quickPassUser, isAuthLoading, fetchNotifications])

  const refresh = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) {
      return
    }

    await fetchNotifications(quickPassUser.id)
  }, [quickPassUser, fetchNotifications])

  const markRead = useCallback(
    async (notificationId: string): Promise<void> => {
      if (quickPassUser === null) {
        return
      }

      setNotifications(previous =>
        previous.map(entry =>
          entry.id === notificationId
            ? { ...entry, isRead: true, readAt: Date.now() }
            : entry,
        ),
      )

      await markNotificationRead(quickPassUser.id, notificationId)
    },
    [quickPassUser],
  )

  const markAllRead = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) {
      return
    }

    const now = Date.now()
    setNotifications(previous =>
      previous.map(entry =>
        entry.isRead === true ? entry : { ...entry, isRead: true, readAt: now },
      ),
    )

    await markAllNotificationsRead(quickPassUser.id)
  }, [quickPassUser])

  const unreadCount = useMemo<number>(
    () => notifications.filter(entry => entry.isRead === false).length,
    [notifications],
  )

  const value = useMemo<QuickPassNotificationsContextValue>(
    () => ({
      notifications,
      unreadCount,
      isLoading,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      refresh,
      markRead,
      markAllRead,
    }),
    [
      notifications,
      unreadCount,
      isLoading,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      refresh,
      markRead,
      markAllRead,
    ],
  )

  return (
    <QuickPassNotificationsContext.Provider value={value}>
      {children}
    </QuickPassNotificationsContext.Provider>
  )
}

export { QuickPassNotificationsProvider }
